import {
  ActionIcon, Badge, Box, Button, Group, Menu, NumberInput, Paper, SegmentedControl, Stack, Switch, Text, Textarea, TextInput, Tooltip,
} from "@mantine/core";
import {
  IconAlignJustified, IconArrowDown, IconArrowUp, IconChevronDown, IconChevronRight, IconDatabase, IconPageBreak, IconGripVertical,
  IconHeading, IconLetterT, IconLineDashed, IconList, IconLock, IconNumbers, IconPlus, IconScale, IconSeparatorHorizontal, IconSignature,
  IconSpacingVertical, IconTable, IconTrash, IconX,
} from "@tabler/icons-react";
import { useRef, useState, type ReactNode } from "react";

import { useT } from "../../i18n/script";

/** Blokovi sadržaja šablona (isti oblik kao u backend/app/modules/reports/template_builder.py). */
export type SystemType = "refs" | "title" | "purpose" | "params" | "content" | "signature" | "legal";
export type TextBlock = { type: "text"; id: string; heading: string; text: string; align: "left" | "justify" | "center" | "right"; style: "normal" | "bold" | "italic" | "small" };
export type TableBlock = { type: "table"; id: string; heading: string; columns: string[]; rows: string[][]; header: boolean };
export type Block =
  | { type: SystemType; id: string }
  | TextBlock
  | TableBlock
  | { type: "spacer"; id: string; height: number }
  | { type: "rule"; id: string }
  | { type: "pagebreak"; id: string };

const SYSTEM: Record<SystemType, { label: string; hint: string; icon: ReactNode }> = {
  refs: { label: "Broj i datum", hint: "Djelovodni broj i datum izdavanja", icon: <IconNumbers size={16} /> },
  title: { label: "Naslov", hint: "Naslov i podnaslov izvještaja", icon: <IconHeading size={16} /> },
  purpose: { label: "Svrha", hint: "Za šta se dokument izdaje (unosi se pri izdavanju)", icon: <IconAlignJustified size={16} /> },
  params: { label: "Parametri", hint: "Izabrani parametri izvještaja (sezona, opština…)", icon: <IconList size={16} /> },
  content: { label: "Podaci izvještaja", hint: "Tabele, grafici i pokazatelji koje pravi izvještaj (obavezno)", icon: <IconDatabase size={16} /> },
  signature: { label: "Potpis i M.P.", hint: "Ko je izdao dokument i mjesto pečata", icon: <IconSignature size={16} /> },
  legal: { label: "Pravni osnov i napomena", hint: "Osnov izdavanja i napomena iz Podešavanja", icon: <IconScale size={16} /> },
};
const OWN = {
  text: { label: "Tekst", icon: <IconLetterT size={16} /> },
  table: { label: "Tabela", icon: <IconTable size={16} /> },
  spacer: { label: "Razmak", icon: <IconSpacingVertical size={16} /> },
  rule: { label: "Linija", icon: <IconSeparatorHorizontal size={16} /> },
  pagebreak: { label: "Novi list", icon: <IconPageBreak size={16} /> },
} as const;

/** Polja koja se sama popune u tekstu i tabeli (zatvorena lista u template_builder.FIELDS). */
export const FIELDS: { key: string; label: string }[] = [
  { key: "datum", label: "datum" }, { key: "broj", label: "broj" }, { key: "naslov", label: "naslov" },
  { key: "podnaslov", label: "podnaslov" }, { key: "svrha", label: "svrha" }, { key: "organizacija", label: "organizacija" },
  { key: "izdao", label: "izdao" }, { key: "funkcija", label: "funkcija" },
];

const uid = () => Math.random().toString(36).slice(2, 10);
const isSystem = (t: Block["type"]): t is SystemType => t in SYSTEM;

function newBlock(type: Block["type"]): Block {
  switch (type) {
    case "text": return { type, id: uid(), heading: "", text: "", align: "justify", style: "normal" };
    case "table": return { type, id: uid(), heading: "", columns: ["R.br.", "Naziv"], rows: [["1.", ""]], header: true };
    case "spacer": return { type, id: uid(), height: 8 };
    case "rule": case "pagebreak": return { type, id: uid() };
    default: return { type, id: type };
  }
}

export default function BlocksEditor({ blocks, onChange }: { blocks: Block[]; onChange: (b: Block[]) => void }) {
  const { t } = useT();
  const [open, setOpen] = useState<string | null>(null);
  const [armed, setArmed] = useState<number | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const key = (b: Block, i: number) => b.id || `${b.type}-${i}`;

  const move = (from: number, to: number) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    const next = [...blocks];
    const [x] = next.splice(from, 1);
    next.splice(to, 0, x);
    onChange(next);
  };
  const insert = (at: number, type: Block["type"]) => {
    const b = newBlock(type);
    const next = [...blocks];
    next.splice(at, 0, b);
    onChange(next);
    if (type === "text" || type === "table" || type === "spacer") setOpen(key(b, at));
  };
  const update = (i: number, b: Block) => onChange(blocks.map((x, j) => (j === i ? b : x)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const present = new Set(blocks.map((b) => b.type));

  const addMenu = (at: number, target: ReactNode) => (
    <Menu position="bottom-start" withinPortal shadow="md">
      <Menu.Target>{target}</Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t("Vaš sadržaj")}</Menu.Label>
        {(Object.keys(OWN) as (keyof typeof OWN)[]).map((k) => (
          <Menu.Item key={k} leftSection={OWN[k].icon} onClick={() => insert(at, k)}>{t(OWN[k].label)}</Menu.Item>
        ))}
        {(Object.keys(SYSTEM) as SystemType[]).some((k) => !present.has(k)) && <Menu.Divider />}
        {(Object.keys(SYSTEM) as SystemType[]).some((k) => !present.has(k)) && <Menu.Label>{t("Dijelovi dokumenta")}</Menu.Label>}
        {(Object.keys(SYSTEM) as SystemType[]).filter((k) => !present.has(k)).map((k) => (
          <Menu.Item key={k} leftSection={SYSTEM[k].icon} onClick={() => insert(at, k)}>{t(SYSTEM[k].label)}</Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <Stack gap={6}>
      <Text size="xs" c="dimmed">{t("Dokument se slaže od blokova, odozgo nadolje, ispod zaglavlja. Blok premjestite prevlačenjem za ručicu ili strelicama, a novi dodajte dugmetom „Dodaj blok“ ili znakom + ispod bloka.")}</Text>
      {blocks.map((b, i) => {
        const k = key(b, i);
        const sys = isSystem(b.type);
        const editable = b.type === "text" || b.type === "table" || b.type === "spacer";
        const expanded = open === k && editable;
        const meta = sys ? SYSTEM[b.type as SystemType] : OWN[b.type as keyof typeof OWN];
        const preview = b.type === "text" ? (b.heading || b.text).slice(0, 60) : b.type === "table" ? (b.heading || b.columns.join(" · ")).slice(0, 60)
          : b.type === "spacer" ? `${b.height} mm` : sys ? t(SYSTEM[b.type as SystemType].hint) : "";
        return (
          <Paper key={k} withBorder radius="md" p={6} draggable={armed === i}
            onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); }}
            onDragOver={(e) => { if (drag !== null) { e.preventDefault(); setOver(i); } }}
            onDragLeave={() => setOver((o) => (o === i ? null : o))}
            onDrop={(e) => { e.preventDefault(); if (drag !== null) move(drag, i); setDrag(null); setOver(null); setArmed(null); }}
            onDragEnd={() => { setDrag(null); setOver(null); setArmed(null); }}
            style={{
              opacity: drag === i ? .45 : 1,
              borderColor: over === i && drag !== i ? "var(--mantine-primary-color-filled)" : undefined,
              background: sys ? "var(--mantine-color-default-hover)" : undefined,
            }}>
            <Group gap={6} wrap="nowrap">
              <ActionIcon variant="subtle" color="gray" size="sm" style={{ cursor: "grab" }} aria-label={t("Prevuci")}
                onMouseDown={() => setArmed(i)} onMouseUp={() => setArmed(null)}><IconGripVertical size={16} /></ActionIcon>
              <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0, cursor: editable ? "pointer" : "default" }}
                onClick={() => editable && setOpen(expanded ? null : k)}>
                {editable ? (expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />) : <Box w={14} />}
                {meta.icon}
                <Text size="sm" fw={600} style={{ whiteSpace: "nowrap" }}>{t(meta.label)}</Text>
                {b.type === "content" && <Tooltip label={t("Obavezan blok")}><IconLock size={13} color="var(--mantine-color-dimmed)" /></Tooltip>}
                <Text size="xs" c="dimmed" truncate="end" style={{ minWidth: 0 }}>{preview}</Text>
              </Group>
              <Group gap={0} wrap="nowrap">
                <ActionIcon variant="subtle" color="gray" size="sm" disabled={i === 0} onClick={() => move(i, i - 1)} aria-label={t("Gore")}><IconArrowUp size={15} /></ActionIcon>
                <ActionIcon variant="subtle" color="gray" size="sm" disabled={i === blocks.length - 1} onClick={() => move(i, i + 1)} aria-label={t("Dolje")}><IconArrowDown size={15} /></ActionIcon>
                {addMenu(i + 1, <Tooltip label={t("Dodaj ispod")}><ActionIcon variant="subtle" color="gray" size="sm" aria-label={t("Dodaj ispod")}><IconPlus size={15} /></ActionIcon></Tooltip>)}
                <ActionIcon variant="subtle" color="red" size="sm" disabled={b.type === "content"} onClick={() => remove(i)} aria-label={t("Ukloni")}><IconTrash size={15} /></ActionIcon>
              </Group>
            </Group>
            {expanded && b.type === "text" && <TextEditor b={b} onChange={(x) => update(i, x)} />}
            {expanded && b.type === "table" && <TableEditor b={b} onChange={(x) => update(i, x)} />}
            {expanded && b.type === "spacer" && (
              <Box px="xl" pt="xs"><NumberInput label={t("Visina razmaka")} suffix=" mm" min={2} max={80} w={160} value={b.height}
                onChange={(v) => typeof v === "number" && update(i, { ...b, height: v })} /></Box>
            )}
          </Paper>
        );
      })}
      <Group>{addMenu(blocks.length, <Button size="xs" variant="light" leftSection={<IconPlus size={15} />}>{t("Dodaj blok")}</Button>)}</Group>
    </Stack>
  );
}

/** Dugmad za ubacivanje polja ({datum}, {broj} …) na mjesto kursora. */
function FieldChips({ onPick }: { onPick: (token: string) => void }) {
  const { t } = useT();
  return (
    <Group gap={4} mt={4}>
      <Text size="xs" c="dimmed">{t("Ubaci polje")}:</Text>
      {FIELDS.map((f) => (
        <Badge key={f.key} size="sm" variant="light" style={{ cursor: "pointer", textTransform: "none" }} onClick={() => onPick(`{${f.key}}`)}>{`{${f.label}}`}</Badge>
      ))}
    </Group>
  );
}

function TextEditor({ b, onChange }: { b: TextBlock; onChange: (b: TextBlock) => void }) {
  const { t } = useT();
  const ref = useRef<HTMLTextAreaElement>(null);
  const insertAtCursor = (token: string) => {
    const el = ref.current;
    const pos = el ? el.selectionStart ?? b.text.length : b.text.length;
    const end = el ? el.selectionEnd ?? pos : pos;
    onChange({ ...b, text: b.text.slice(0, pos) + token + b.text.slice(end) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(pos + token.length, pos + token.length); } });
  };
  return (
    <Stack gap="xs" px="xl" pt="xs" pb={4}>
      <TextInput label={t("Podnaslov bloka")} description={t("Nije obavezno")} maxLength={200} value={b.heading} onChange={(e) => onChange({ ...b, heading: e.currentTarget.value })} />
      <Box>
        <Textarea ref={ref} label={t("Tekst")} description={t("Prazan red između dva dijela teksta pravi novi pasus.")} autosize minRows={4} maxRows={16} maxLength={5000}
          value={b.text} onChange={(e) => onChange({ ...b, text: e.currentTarget.value })} />
        <FieldChips onPick={insertAtCursor} />
      </Box>
      <Box><Text size="sm" fw={500} mb={4}>{t("Poravnanje")}</Text>
        <SegmentedControl fullWidth size="xs" value={b.align} onChange={(v) => onChange({ ...b, align: v as TextBlock["align"] })}
          data={[{ value: "left", label: t("Lijevo") }, { value: "justify", label: t("Obostrano") }, { value: "center", label: t("Centar") }, { value: "right", label: t("Desno") }]} /></Box>
      <Text size="sm" fw={500} mb={-4}>{t("Stil teksta")}</Text>
      <SegmentedControl fullWidth size="xs" value={b.style} onChange={(v) => onChange({ ...b, style: v as TextBlock["style"] })}
        data={[{ value: "normal", label: t("Obično") }, { value: "bold", label: t("Podebljano") }, { value: "italic", label: t("Kurziv") }, { value: "small", label: t("Sitno") }]} />
    </Stack>
  );
}

function TableEditor({ b, onChange }: { b: TableBlock; onChange: (b: TableBlock) => void }) {
  const { t } = useT();
  const n = b.columns.length;
  const setCell = (r: number, c: number, v: string) => onChange({ ...b, rows: b.rows.map((row, i) => (i === r ? row.map((x, j) => (j === c ? v : x)) : row)) });
  const cellStyle = { input: { fontSize: 12 } };
  return (
    <Stack gap="xs" px="xl" pt="xs" pb={4}>
      <TextInput label={t("Podnaslov tabele")} description={t("Nije obavezno")} maxLength={200} value={b.heading} onChange={(e) => onChange({ ...b, heading: e.currentTarget.value })} />
      <Switch label={t("Prvi red je zaglavlje tabele")} checked={b.header} onChange={(e) => onChange({ ...b, header: e.currentTarget.checked })} />
      <Box style={{ overflowX: "auto" }}>
        <Box component="table" style={{ borderCollapse: "collapse", width: "100%" }}>
          <tbody>
            {(
              <tr style={{ opacity: b.header ? 1 : .45 }} title={b.header ? undefined : t("Zaglavlje se ne prikazuje u dokumentu")}>
                {b.columns.map((c, j) => (
                  <td key={j} style={{ padding: 2, minWidth: 110 }}>
                    <TextInput size="xs" value={c} maxLength={100} styles={{ input: { fontWeight: 700, fontSize: 12 } }}
                      onChange={(e) => onChange({ ...b, columns: b.columns.map((x, k) => (k === j ? e.currentTarget.value : x)) })}
                      rightSection={n > 1 ? <ActionIcon size="xs" variant="subtle" color="gray" aria-label={t("Ukloni kolonu")}
                        onClick={() => onChange({ ...b, columns: b.columns.filter((_, k) => k !== j), rows: b.rows.map((r) => r.filter((_, k) => k !== j)) })}><IconX size={12} /></ActionIcon> : undefined} />
                  </td>
                ))}
                <td />
              </tr>
            )}
            {b.rows.map((row, i) => (
              <tr key={i}>
                {row.map((v, j) => (
                  <td key={j} style={{ padding: 2, minWidth: 110 }}>
                    <TextInput size="xs" value={v} maxLength={500} styles={cellStyle} onChange={(e) => setCell(i, j, e.currentTarget.value)} />
                  </td>
                ))}
                <td style={{ width: 28 }}>
                  <ActionIcon size="sm" variant="subtle" color="red" aria-label={t("Ukloni red")} onClick={() => onChange({ ...b, rows: b.rows.filter((_, k) => k !== i) })}><IconTrash size={14} /></ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <Group gap="xs">
        <Button size="compact-xs" variant="light" leftSection={<IconPlus size={13} />} disabled={b.rows.length >= 60}
          onClick={() => onChange({ ...b, rows: [...b.rows, Array(n).fill("")] })}>{t("Red")}</Button>
        <Button size="compact-xs" variant="light" leftSection={<IconPlus size={13} />} disabled={n >= 8}
          onClick={() => onChange({ ...b, columns: [...b.columns, `${t("Kolona")} ${n + 1}`], rows: b.rows.map((r) => [...r, ""]) })}>{t("Kolona")}</Button>
        <Text size="xs" c="dimmed">{t("Najviše 8 kolona i 60 redova. U ćelije se mogu upisati i polja, npr. {datum}.")}</Text>
      </Group>
      <Box><Text size="xs" c="dimmed"><IconLineDashed size={12} /> {t("Izgled linija i zaglavlja tabele bira se u sekciji „Tabele“.")}</Text></Box>
    </Stack>
  );
}
